import IProcedureDetail from '@Shared.Angular/@types/core/contracts/queryModel/procedure/procedureDetail';
import React, { ChangeEvent, useState } from 'react';
import ProcedureActionEditor from './ProcedureActionEditor';
import {
  Editor,
  EditorChangeEvent,
  EditorMountEvent,
  EditorTools
} from '@progress/kendo-react-editor';
import { SharedAngular } from '@Client/@types/sharedAngular';
import { useService } from '@Client/runner.hooks/useService';
import IProcedureActionDetail from '@Shared.Angular/@types/core/contracts/queryModel/procedure/procedureActionDetail';
import { Guid } from '@Shared.Angular/@types/guid';

type Props = {
  procedure: IProcedureDetail;
};

const ProcedureEditor = (props: Props) => {
  const [procedure, setProcedure] = useState(() => props.procedure);
  const [editedProcedure, setEditedProcedure] = useState({ ...procedure });
  const [isEditMode, setIsEditMode] = useState(false);

  const updateName = (e: ChangeEvent<HTMLInputElement>) => {
    setEditedProcedure({ ...editedProcedure, name: e.target.value });
  };
  const updateDescription = (e: EditorChangeEvent) => {
    setEditedProcedure({ ...editedProcedure, description: e.html });
  };
  const procedureApiService = useService<SharedAngular.ProcedureApiService>(
    'procedureApiService'
  );
  const saveChanges = () => {
    procedureApiService.updateProcedure(editedProcedure);
    setProcedure({ ...editedProcedure });
    setIsEditMode(false);
  };
  const cancelChanges = () => {
    setEditedProcedure({ ...procedure });
    setIsEditMode(false);
  };
  const actionUpdated = (updatedAction: IProcedureActionDetail) => {
    setProcedure((prev) => ({
      ...prev,
      actions: prev.actions.map((action) =>
        action.order === updatedAction.order ? updatedAction : action
      )
    }));
  };

  const addEditorContentStyles = (event: EditorMountEvent) => {
    const iframeDocument = event.dom.ownerDocument;
    const style = iframeDocument.createElement('style');
    style.appendChild(
      iframeDocument.createTextNode(
        'body{font-family:"Open Sans", sans-serif;font-size:13px;}'
      )
    );
    iframeDocument.head.appendChild(style);
  };

  const tocItemClicked = (actionId: Guid) => {
    document
      .getElementById('action_' + actionId)
      .scrollIntoView({ behavior: 'smooth' });
  };

  const tocKeyPress = (event: React.KeyboardEvent, actionId: Guid) => {
    if (event.key === 'Enter') {
      tocItemClicked(actionId);
    }
  };

  const editorTools = [
    EditorTools.Bold,
    EditorTools.Italic,
    EditorTools.Underline,
    EditorTools.Strikethrough,
    EditorTools.Subscript,
    EditorTools.Superscript,
    EditorTools.ForeColor,
    EditorTools.BackColor,
    EditorTools.CleanFormatting,
    EditorTools.AlignLeft,
    EditorTools.AlignCenter,
    EditorTools.AlignRight,
    EditorTools.AlignJustify,
    EditorTools.Indent,
    EditorTools.Outdent,
    EditorTools.OrderedList,
    EditorTools.UnorderedList,
    EditorTools.NumberedList,
    EditorTools.BulletedList,
    EditorTools.FontSize,
    EditorTools.FormatBlock,
    EditorTools.Link,
    EditorTools.Unlink,
    EditorTools.InsertImage,
    EditorTools.ViewHtml
  ];

  return (
    procedure && (
      <div id="editor-container">
        <link
          href="https://kendo.cdn.telerik.com/themes/7.0.1/material/material-main.css"
          rel="stylesheet"
          type="text/css"
        />
        <div className="table-of-contents">
          <div className="toc-heading">Table of Contents</div>
          <div className="toc-items">
            {procedure.actions
              .filter((action) => action.active)
              .map((action, index) => (
                <div
                  className="toc-item"
                  key={action.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => tocItemClicked(action.id)}
                  onKeyDown={(e) => tocKeyPress(e, action.id)}
                >
                  <div className="toc-index">
                    {index < 9 ? '0' : ''}
                    {index + 1}
                  </div>
                  <div className="toc-title">{action.title}</div>
                </div>
              ))}
          </div>
        </div>

        <div className="editors">
          {isEditMode ? (
            <div className="editor">
              <div className="procedure-field">
                <label htmlFor="procedure-name">Name</label>
                <input
                  type="text"
                  id="procedure-name"
                  value={editedProcedure.name}
                  onChange={updateName}
                ></input>
              </div>
              <div className="procedure-field">
                <label>Description</label>
                <Editor
                  tools={editorTools}
                  defaultContent={editedProcedure.description || ''}
                  onChange={updateDescription}
                  onMount={addEditorContentStyles}
                  resizable={true}
                  contentStyle={{ height: '100%' }}
                  style={{ resize: 'vertical' }}
                />
              </div>
              <div className="editor-buttons">
                <button className="btn right" onClick={saveChanges}>
                  Save
                </button>
                <button
                  className="btn btn-text-only right"
                  onClick={cancelChanges}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="editor-readonly">
              <div
                className="editor-hover-button fa-light fa-pencil"
                title="Edit"
                onClick={() => setIsEditMode(true)}
              ></div>
              <div className="procedure-field">
                <h3>{procedure.name}</h3>
              </div>
              <div
                dangerouslySetInnerHTML={{
                  __html: procedure.description
                }}
              ></div>
            </div>
          )}

          {procedure.actions
            .filter((action) => action.active)
            .map((action, index) => (
              <ProcedureActionEditor
                key={action.order}
                index={index + 1}
                action={action}
                editorTools={editorTools}
                addEditorContentStyles={addEditorContentStyles}
                persistChanges={actionUpdated}
              ></ProcedureActionEditor>
            ))}
        </div>
      </div>
    )
  );
};

export default ProcedureEditor;
